import React, { Suspense } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { RouteFallback } from '../components/common';
import map from 'lodash/map';
import { Button } from '@ai-content-hub/content-kit';
import {
    FileText,
    ListChecks,
    FileQuestion,
    Home,
    Type,
    Image,
    ImagePlus,
    FileEdit,
    Music,
    Video,
    CircleHelp,
    LayoutTemplate,
    BookOpen,
    Link2,
    Shuffle,
    Palette,
    MapPin,
    FileStack,
    PenLine,
    TextCursorInput,
    ListOrdered,
    Mic,
    Volume2,
    AudioLines,
    ChevronsDownUp,
    GripHorizontal,
    GraduationCap,
    Box,
    type LucideIcon,
} from 'lucide-react';

interface ContentTypeMenuItem {
    type: string;
    label: string;
    icon: LucideIcon;
}

// Element content types (basic building blocks)
const elementTypes: ContentTypeMenuItem[] = [
    { type: 'text', label: 'Text', icon: Type },
    { type: 'image', label: 'Image', icon: Image },
    { type: 'rich-text', label: 'Rich Text', icon: FileEdit },
    { type: 'audio', label: 'Audio', icon: Music },
    { type: 'video', label: 'Video', icon: Video },
    { type: 'media', label: 'Media', icon: ImagePlus },
    { type: 'mcq', label: 'MCQ', icon: CircleHelp },
    { type: 'match', label: 'Match', icon: Link2 },
    { type: 'color-drop-zone', label: 'Color Drop Zone', icon: Palette },
    { type: 'fill-in-the-blanks', label: 'Fill in Blanks', icon: TextCursorInput },
    { type: 'ordering', label: 'Ordering', icon: ListOrdered },
    { type: 'record-audio', label: 'Record Audio', icon: Mic },
    { type: 'page', label: 'Page', icon: LayoutTemplate },
];

// Composite content types (advanced content)
const compositeTypes: ContentTypeMenuItem[] = [
    { type: 'mcq-set', label: 'MCQ Set', icon: ListChecks },
    { type: 'question-set', label: 'Question Set', icon: ListOrdered },
    { type: 'qt-dropdown', label: 'Dropdown', icon: ChevronsDownUp },
    { type: 'qt-drag-the-words', label: 'Drag the Words', icon: GripHorizontal },
    { type: 'speaking', label: 'Speaking', icon: Volume2 },
    { type: 'interactive-reading', label: 'Interactive Reading', icon: AudioLines },
    { type: 'quiz', label: 'Quiz', icon: FileQuestion },
    { type: 'lesson-mcq', label: 'Lesson MCQ', icon: BookOpen },
    { type: 'rich-matching', label: 'Rich Matching', icon: Shuffle },
    { type: 'image-hotspot', label: 'Image Hotspot', icon: MapPin },
    { type: 'essay', label: 'Essay', icon: PenLine },
    { type: 'documents', label: 'Documents', icon: FileStack },
    { type: '3d', label: '3D Model', icon: Box },
    { type: 'ordering-content', label: 'Ordering', icon: ListOrdered },
    { type: 'fill-in-the-blanks-content', label: 'Fill in Blanks', icon: TextCursorInput },
];

// SCORM content types
const scormTypes: ContentTypeMenuItem[] = [
    { type: 'scorm-12', label: 'SCORM 1.2', icon: GraduationCap },
    { type: 'scorm-2004', label: 'SCORM 2004', icon: GraduationCap },
];

export const EditorLayout: React.FC = () => {
    const location = useLocation();

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
                <div className="container flex h-16 items-center justify-between px-4">
                    <div className="flex items-center gap-6">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                                <FileText className="h-5 w-5" />
                            </div>
                            <span className="text-xl font-bold font-heading">Content Editor</span>
                        </Link>
                    </div>

                    <nav className="flex items-center gap-2">
                        <Link to="/">
                            <Button
                                variant={location.pathname === '/' ? 'default' : 'ghost'}
                                size="sm"
                            >
                                <Home className="h-4 w-4 mr-2" />
                                Home
                            </Button>
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Sidebar + Content */}
            <div className="flex">
                {/* Sidebar */}
                <aside className="sticky top-16 h-[calc(100vh-4rem)] w-64 border-r bg-card p-4 overflow-y-auto">
                    <div className="space-y-6">
                        {/* Elements Group */}
                        <div className="space-y-2">
                            <h3 className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Elements
                            </h3>
                            {map(elementTypes, ({ type, label, icon: Icon }) => {
                                const isActive = location.pathname.startsWith(`/edit/`) && location.search === `?type=${type}`;
                                return (
                                    <Link key={type} to={`/edit/new?type=${type}`}>
                                        <Button
                                            variant={isActive ? 'default' : 'ghost'}
                                            size="sm"
                                            className="w-full justify-start"
                                        >
                                            <Icon className="h-4 w-4 mr-2" />
                                            {label}
                                        </Button>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Content Types Group */}
                        <div className="space-y-2">
                            <h3 className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Content Types
                            </h3>
                            {map(compositeTypes, ({ type, label, icon: Icon }) => {
                                const isActive = location.pathname.startsWith(`/edit/`) && location.search === `?type=${type}`;
                                return (
                                    <Link key={type} to={`/edit/new?type=${type}`}>
                                        <Button
                                            variant={isActive ? 'default' : 'ghost'}
                                            size="sm"
                                            className="w-full justify-start"
                                        >
                                            <Icon className="h-4 w-4 mr-2" />
                                            {label}
                                        </Button>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* SCORM Group */}
                        <div className="space-y-2">
                            <h3 className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                SCORM
                            </h3>
                            {map(scormTypes, ({ type, label, icon: Icon }) => {
                                const isActive = location.pathname.startsWith(`/edit/`) && location.search === `?type=${type}`;
                                return (
                                    <Link key={type} to={`/edit/new?type=${type}`}>
                                        <Button
                                            variant={isActive ? 'default' : 'ghost'}
                                            size="sm"
                                            className="w-full justify-start"
                                        >
                                            <Icon className="h-4 w-4 mr-2" />
                                            {label}
                                        </Button>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-6 h-[calc(100vh-4rem)] overflow-y-auto">
                    {/* EAA-1670: inner Suspense so navigating between lazy pages under
                        this layout keeps the sidebar/header mounted instead of falling
                        back to App.tsx's full-screen boundary. */}
                    <Suspense fallback={<RouteFallback />}>
                        <Outlet />
                    </Suspense>
                </main>
            </div>
        </div>
    );
};

export default EditorLayout;
